// Angular imports
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

// 3rd party imports
import { DxPopupComponent } from 'devextreme-angular';
import moment from 'moment-timezone';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';

// Model imports
import { AnnouncementsParametersDto } from '../../_models/announcements-parameters-dto';
import { AnnouncementsResultDto } from '../../_models/announcements-result-dto';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-portal-announcement',
  templateUrl: './portal-announcement.component.html',
  styleUrls: ['./portal-announcement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PortalAnnouncementComponent implements OnInit, OnDestroy {
  // Dimension constants
  private readonly DESKTOP_WIDTH = '665px';
  private readonly DESKTOP_HEIGHT = '720px';
  private readonly MOBILE_WIDTH_CALC = 'calc(100% - 48px)';
  private readonly MOBILE_HEIGHT_CALC =
    'calc(100dvh - 50px - 48px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))';
  private readonly DESKTOP_OFFSET = '0 30';
  private readonly MOBILE_OFFSET = '0 25';

  @ViewChild('portalAnnouncementPopup', { static: false })
  portalAnnouncementPopup!: DxPopupComponent;

  isSmallScreen: boolean = false;
  loadingVisible: boolean = false;
  portalAnnouncementVisible: boolean = false;
  popupWidth: string = this.DESKTOP_WIDTH;
  popupHeight: string = this.DESKTOP_HEIGHT;
  popupMaxWidth: string = this.DESKTOP_WIDTH;
  popupMaxHeight: string = this.DESKTOP_HEIGHT;
  popupPosition: { my: string; at: string; of: Window; offset: string } = {
    my: 'center',
    at: 'center',
    of: window,
    offset: this.DESKTOP_OFFSET
  };
  announcements: AnnouncementsResultDto[] = [];

  private subscriptions: Subscription[] = [];
  private announcementsSubscription: Subscription | null = null;

  constructor(
    private screenService: ScreenService,
    private userService: UserService,
    private helperService: HelperService,
    private errorService: ServerErrorService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateScreenSize();
    this.subscriptions.push(
      RxjsUtils.safeSubscribe(this.screenService.changed, () =>
        this.updateScreenSize()
      )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.announcementsSubscription?.unsubscribe();
  }

  show(): void {
    this.portalAnnouncementVisible = true;
    this.cdr.markForCheck();
  }

  hide(): void {
    this.portalAnnouncementVisible = false;
    this.cdr.markForCheck();
  }

  repaint(): void {
    this.portalAnnouncementPopup?.instance?.repaint();
  }

  populatePortalAnnouncements(): void {
    // Cancel any pending request before starting a new one
    this.announcementsSubscription?.unsubscribe();

    const param: AnnouncementsParametersDto = new AnnouncementsParametersDto();
    this.loadingVisible = true;
    param.clearCache = true;
    this.announcementsSubscription = RxjsUtils.safeSubscribe(
      this.userService.getAnnouncements(param),
      (announcements) => {
        this.announcements = [...announcements];
        this.loadingVisible = false;
        this.cdr.markForCheck();
      },
      (error) => {
        this.errorService.displayError(error);
        this.loadingVisible = false;
        this.cdr.markForCheck();
      }
    );
  }

  public getFormattedDate(releaseDate: string | Date): string {
    const userTimezone = this.helperService.getUserTimezone();
    return moment.tz(releaseDate, userTimezone).format('MM/DD/YYYY');
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
    if (this.isSmallScreen) {
      this.popupWidth = this.MOBILE_WIDTH_CALC;
      this.popupHeight = this.MOBILE_HEIGHT_CALC;
      this.popupMaxWidth = this.MOBILE_WIDTH_CALC;
      this.popupMaxHeight = this.MOBILE_HEIGHT_CALC;
      this.popupPosition = {
        ...this.popupPosition,
        offset: this.MOBILE_OFFSET
      };
    } else {
      this.popupWidth = this.DESKTOP_WIDTH;
      this.popupHeight = this.DESKTOP_HEIGHT;
      this.popupMaxWidth = this.DESKTOP_WIDTH;
      this.popupMaxHeight = this.DESKTOP_HEIGHT;
      this.popupPosition = {
        ...this.popupPosition,
        offset: this.DESKTOP_OFFSET
      };
    }
    this.cdr.markForCheck();
  }
}
